import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CancelOutlined,
  MoreHoriz,
} from "@mui/icons-material";
import {
  apiCallPrefAgeGroupList,
  apiCallPrefAgeGroupAdd,
  apiCallPrefAgeGroupUpdate,
  apiCallPrefAgeGroupStatus,
} from "../../../../Core/Apicall";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { clearShowMenu } from '../../../../Redux/Reducers/GlobalVar'
import DeletePopup from "../../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../../Common/CommonStatus/CommonStatus";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { toast } from 'react-toastify';

interface DayType {
  _id?: string;
  ageGroupName: string;
  displayOrder: number | string;
  minAge?: number | string;
  maxAge?: number | string;
  isActive: boolean;
  isDelete: boolean;
}

function AgeGroups() {
  const [data, setData] = useState<DayType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>("");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const showMenu: any = useSelector((state: RootState) => state.global.showMenu);
  const [isCreateNew, setIsCreateNew] = useState<boolean>(false);

  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  const [formData, setFormData] = useState<DayType>({
    _id: "",
    ageGroupName: "",
    displayOrder: "",
    minAge: "",
    maxAge: "",
    isActive: true,
    isDelete: false,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<DayType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchDays = async () => {
      setLoading(true);
      try {
        const response = await apiCallPrefAgeGroupList("");
        if (response?.data) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (err: any) {
      } finally {
        setLoading(false);
      }
    };
    fetchDays();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate age group name
    if (!formData.ageGroupName.trim()) {
      toast.error("Age Group Name is required");
      return;
    }
    
 
    
    // Prepare data for API
    const submitData: any = {
      ageGroupName: formData.ageGroupName,
      displayOrder: Number(formData.displayOrder),
    };
    
    // Only include minAge and maxAge if they have values (completely optional)
    if (formData.minAge !== "" && formData.minAge !== undefined && formData.minAge !== null) {
      submitData.minAge = Number(formData.minAge);
    }
    if (formData.maxAge !== "" && formData.maxAge !== undefined && formData.maxAge !== null) {
      submitData.maxAge = Number(formData.maxAge);
    }
    
    if (formData._id) {
      submitData.id = formData._id;
    }
    
    try {
      if (formData._id) {
        await apiCallPrefAgeGroupUpdate(submitData);
        toast.success("Age Group updated successfully!");
      } else {
        await apiCallPrefAgeGroupAdd(submitData);
        toast.success("Age Group saved successfully!");
      }

      setIsCreateNew(false);

      setFormData({
        _id: "",
        ageGroupName: "",
        displayOrder: "",
        minAge: "",
        maxAge: "",
        isActive: true,
        isDelete: false,
      });
      const response = await apiCallPrefAgeGroupList("");
      setData(response?.data || []);
    } catch (err: any) {
      console.error("Error:", err);
      toast.error(err?.response?.data?.message || "Failed to save Age Group");
    }
  };

  const handleStatusUpdate = async (item: DayType) => {
    try {
      await apiCallPrefAgeGroupStatus({
        id: item._id,
        isActive: !item.isActive,
      });
      setData((prev) =>
        prev.map((m) =>
          m._id === item._id ? { ...m, isActive: !item.isActive } : m
        )
      );
      setStatusDialogOpen(false)
    } catch (err) {
    }
  };

  const handleDelete = async () => {
    if (!menuVibe) return
    try {
      await apiCallPrefAgeGroupStatus({
        id: menuVibe._id,
        isDelete: true,
      })
      setData(prev => prev.filter(item => item._id !== menuVibe._id))
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false)
      setMenuVibe(null)
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    setAnchorEl(event.currentTarget)
    setMenuVibe(type)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            ...menuVibe,
            _id: menuVibe._id,
            minAge: menuVibe.minAge || "",
            maxAge: menuVibe.maxAge || "",
          });
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    }

  ];

  return (
    <Box style={{ paddingBottom: "20px" }}>

      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e:any) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
          placeholder="Search Age Group"
        />
        <Button
          className="btn-black p-2 px-3"
          onClick={() => {
            setFormData({
              _id: "",
              ageGroupName: "",
              displayOrder: "",
              minAge: "",
              maxAge: "",
              isActive: true,
              isDelete: false,
            })
            setIsCreateNew(true)
          }}
        >
          + Create New
        </Button>
      </Box>

      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >

        <TableContainer className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Display Order</TableCell>
                <TableCell>Age Group Name</TableCell>
                <TableCell>Min Age</TableCell>
                <TableCell>Max Age</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .filter(
                  (item) =>
                    search === "" ||
                    item.ageGroupName.toLowerCase().includes(search.toLowerCase())
                )
                .map((item, index: any) => (
                  <TableRow hover key={index}>
                    <TableCell>{item.displayOrder}</TableCell>
                    <TableCell>{item.ageGroupName}</TableCell>
                    <TableCell>{item.minAge || "-"}</TableCell>
                    <TableCell>{item.maxAge || "-"}</TableCell>
                    <TableCell>
                      <Button
                        className={item.isActive ? "btn-success" : "btn-danger"}
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.ageGroupName}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e:any) => handleMenuOpen(e, item)}>
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {loading &&
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>}
        </TableContainer>

      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this age group?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false)
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: { width: "35%", borderRadius: "16px 0 0 16px", overflow: "hidden" },
        }}
      >
        <Box
          className="day-form"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          {/* ---------- Header ---------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData._id ? "Edit Age Group" : "Create Age Group"}
            </Typography>
            <IconButton onClick={() => {
              setIsCreateNew(false)
              dispatch(clearShowMenu());
            }}>
              <CancelOutlined />
            </IconButton>
          </Box>

          {/* ---------- Form ---------- */}
          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              {/* Age Group Name */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Age Group Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  name="ageGroupName"
                  value={formData.ageGroupName}
                  onChange={handleChange}
                  placeholder="Enter Age Group Name (e.g., 18-21, 40s, 50s, no preference)"
                  fullWidth
                  required
                />
              </FormControl>

              {/* Display Order */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Display Order <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  placeholder="Enter Display Order"
                  fullWidth

                  inputProps={{ min: 0, step: 1 }}
                />
              </FormControl>

              {/* Min Age - Fully Optional */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Min Age (Years) <span style={{ fontSize: '12px', color: '#666' }}>(Optional)</span>
                </Typography>
                <TextField
                  name="minAge"
                  type="number"
                  value={formData.minAge}
                  onChange={handleChange}
                  placeholder="Enter Minimum Age (Optional)"
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                />
              </FormControl>

              {/* Max Age - Fully Optional */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Max Age (Years) <span style={{ fontSize: '12px', color: '#666' }}>(Optional)</span>
                </Typography>
                <TextField
                  name="maxAge"
                  type="number"
                  value={formData.maxAge}
                  onChange={handleChange}
                  placeholder="Enter Maximum Age (Optional)"
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                />
              </FormControl>

              {/* Info Box */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  💡 <strong>Note:</strong> Min and Max values are completely optional. 
                  You can leave them empty for categories like "40s", "50s", "no preference".
                </Typography>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default AgeGroups;